/* global rwgps */
import React, { useState, useMemo, useRef } from 'react'
import styles from './TRouteSurfaces.scss'
import { withTRouteMetrics } from '../../TRouteMetricsContext'
import { Popover } from 'common/styleguide/Popover'
import { FullBleedList } from 'common/FullBleedList'
import { Distance } from 'common/units'
import { Icon } from 'common/Icon'
import { useHover } from 'lib/hooks/useHover'
import { useFormatMessage, Msg, featureNames, commonCTAs } from 'lib/localization'
import { defineMessages } from 'react-intl'
import _ from 'lodash'
import cx from 'classnames'

// NOTE: these MUST be kept in sync with lib/surface_types.rb
export const PAVED = 0
export const UNPAVED = 56

export const messages = defineMessages({
  surfaces: {
    id: 'TRouteSurfaces.surfaces',
    defaultMessage: 'Surfaces',
    description: 'As in road surfaces'
  },
  paved: {
    id: 'TRouteSurfaces.paved',
    defaultMessage: 'Paved',
    description: 'A paved surface, like a road'
  },
  unpaved: {
    id: 'TRouteSurfaces.unpaved',
    defaultMessage: 'Unpaved',
    description: 'An unpaved surface, like a trail'
  },
  unknown: {
    id: 'TRouteSurfaces.unknown',
    defaultMessage: 'unknown'
  },
  viewInfo: {
    id: 'TRouteSurfaces.viewInfo',
    defaultMessage: 'View surface information as you plan and view routes'
  },
  infoBullet1: {
    id: 'TRouteSurfaces.infoBullet1',
    defaultMessage: 'Paved surfaces include asphalt, concrete, and chip seal.'
  },
  infoBullet2: {
    id: 'TRouteSurfaces.infoBullet2',
    defaultMessage: 'Unpaved surfaces include gravel, dirt, and natural trails.'
  },
  infoBullet3: {
    id: 'TRouteSurfaces.infoBullet3',
    defaultMessage: 'Unknown surfaces do not have sufficient data to determine the surface type.'
  }
})

// hand me the result of a mc.calculateSurfaceTypes call, i'll do the rest
export const _TRouteSurfaces = ({
  hideZeros, // don't show rows with d === 0
  surfaceTypes,
  groupedSurfaces,
  totalDistance,
  infoPlacement
}) => {
  const triggerRef = useRef(null)
  const [hoverRef, isHovered] = useHover()
  const [showInfo, setShowInfo] = useState(false)
  const fm = useFormatMessage()
  if (!groupedSurfaces) {
    return null // guard
  }

  return (
    <div className={styles.TRouteSurfaces}>
      <div className={styles.header}>
        <h2>{fm(messages.surfaces)}</h2>
        <span ref={hoverRef}>
          <Icon
            i={`ios-information-circle${!(showInfo || isHovered) ? '-outline' : ''}`}
            size={20}
            ref={triggerRef}
            onClick={() => setShowInfo(true)}
            className={cx(styles.infoIcon, showInfo && styles.showInfo)}
          />
        </span>
        {showInfo && <InfoPopover
          placement={infoPlacement ?? 'top'}
          target={triggerRef}
          onRequestClose={() => setShowInfo(false)}
          fm={fm}
        />}
      </div>
      <FullBleedList interactive={false}>
        {Object.keys(groupedSurfaces).map(group => group && <SurfaceRow
          key={group}
          fm={fm}
          group={group}
          hideZeros={hideZeros}
          surfaces={groupedSurfaces[group]}
          surfaceTypes={surfaceTypes}
          totalDistance={totalDistance}
        />)}
      </FullBleedList>
    </div>
  )
}

const InfoPopover = ({ placement, target, onRequestClose, fm }) => <Popover
  placement={placement}
  target={target}
  onRequestClose={onRequestClose}
>
  <h3>{fm(featureNames.surfaceTypes)}</h3>
  {fm(messages.viewInfo)}:
  <ul>
    <li>{fm(messages.infoBullet1)}</li>
    <li>{fm(messages.infoBullet2)}</li>
    <li>{fm(messages.infoBullet3)}</li>
  </ul>
  <a href='/help/surface-types' target='_blank'>{fm(commonCTAs.learnMore)}</a>
</Popover>

export const TRouteSurfaces = withTRouteMetrics(props => {
  const { groupedSurfaces } = rwgps.surfaces
  const [hasSurface, setHasSurface] = useState(false)

  useMemo(() => {
    if (_.some(props.trackPoints, tp => tp.surface)) {
      setHasSurface(true)
    }
  }, [props.trackPoints?.length])

  if (!props.surfaceTypes || !hasSurface) {
    // no mc yet, or no surface
    return null
  }
  return <_TRouteSurfaces {...props} groupedSurfaces={groupedSurfaces} />
})

const surfaceIcons = {
  paved: <div className={styles.bg} />,
  unpaved: <div className={styles.bg}>
    <div className={styles.square} />
    <div className={styles.square} />
  </div>,
  unknown: <div className={styles.bg}>
    <div className={styles.line} />
  </div>
}

const SurfaceRow = ({ fm, group, hideZeros, surfaces, surfaceTypes, totalDistance }) => {
  const value = Object.keys(surfaces).reduce((acc, cur) => {
    if (surfaceTypes[cur]) {
      acc += surfaceTypes[cur]
    }
    return acc
  }, 0)

  if (value === 0 && hideZeros) {
    return null
  }

  return <li className={styles.surfaceRow}>
    {surfaceIcons[group]}
    <div className={styles.sGroup}><Msg m={messages[group]} capitalized /></div>
    <div className={styles.sDistance}><Distance bigUnits short value={value} /></div>
    <div className={styles.tDistance}>{totalDistance ? Math.round(value / totalDistance * 100) : '- -'}%</div>
  </li>
}
